// ---------------------------------------------------------------------------
// ChatWidget — Floating chat bubble + expanded panel
//
// A bottom-right FAB that expands to an inline chat panel. Communicates
// with the UITL chat API through the shared UITLApiClient from context.
//
// Features:
//   - Floating action button with chat icon
//   - Expandable chat panel with header, message list, and input
//   - Shows persona_name and welcome_message from remote config
//   - Auto-scrolls to latest message
//   - Loading dots while waiting for AI response
//   - Fully collapsible / expandable
//   - All inline styles, no external CSS
// ---------------------------------------------------------------------------

import React, { useCallback, useEffect, useRef, useState } from 'react';
import type { ChatMessage, ChatSession } from '../types';
import { useUITL } from './UITLProvider';
import type { UITLResolvedTheme } from './UITLProvider';

export interface ChatWidgetProps {
  /** Override positioning. Defaults to the config value or 'bottom-right'. */
  position?: 'bottom-right' | 'bottom-left';
  /** Custom title shown in the chat header. */
  title?: string;
  /** Offset from the bottom edge in pixels. Defaults to 20. */
  bottomOffset?: number;
}

export function ChatWidget({
  position,
  title,
  bottomOffset = 20,
}: ChatWidgetProps) {
  const { client, config, remoteConfig, theme } = useUITL();
  const resolvedPosition =
    position ?? config.position?.chat ?? 'bottom-right';
  const chatTitle =
    title ?? remoteConfig?.chat?.persona_name ?? 'Chat with us';
  const isRight = resolvedPosition === 'bottom-right';

  // State
  const [open, setOpen] = useState(false);
  const [session, setSession] = useState<ChatSession | null>(null);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Refs
  const bottomRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Auto-scroll to bottom when messages change
  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, sending]);

  // Focus input when panel opens
  useEffect(() => {
    if (open) {
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [open]);

  // Create or restore session on first open
  const ensureSession = useCallback(async () => {
    if (session) return session;
    try {
      const s = await client.createChatSession();
      setSession(s);
      if (s.messages.length) {
        setMessages(s.messages);
      } else if (s.welcome_message) {
        setMessages([
          {
            role: 'assistant',
            content: s.welcome_message,
            timestamp: new Date().toISOString(),
          },
        ]);
      }
      return s;
    } catch (err) {
      console.error('[UITL SDK] Failed to create chat session:', err);
      setError('Unable to start chat. Please try again.');
      return null;
    }
  }, [client, session]);

  const handleOpen = useCallback(async () => {
    setOpen(true);
    setError(null);
    await ensureSession();
  }, [ensureSession]);

  const handleSend = useCallback(async () => {
    const text = input.trim();
    if (!text || sending) return;

    setError(null);
    const currentSession = session ?? (await ensureSession());
    if (!currentSession) return;

    const userMsg: ChatMessage = {
      role: 'user',
      content: text,
      timestamp: new Date().toISOString(),
    };
    setMessages((prev) => [...prev, userMsg]);
    setInput('');
    setSending(true);

    try {
      const res = await client.sendChatMessage(
        currentSession.session_id,
        text,
      );
      setMessages((prev) => [
        ...prev,
        {
          role: 'assistant',
          content: res.reply,
          timestamp: new Date().toISOString(),
        },
      ]);
    } catch (err) {
      console.error('[UITL SDK] Chat send error:', err);
      setMessages((prev) => [
        ...prev,
        {
          role: 'assistant',
          content: 'Sorry, something went wrong. Please try again.',
          timestamp: new Date().toISOString(),
        },
      ]);
    } finally {
      setSending(false);
    }
  }, [client, ensureSession, input, sending, session]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        handleSend();
      }
    },
    [handleSend],
  );

  // Style helpers
  const t = theme;

  // ---------- Collapsed: FAB button ----------
  if (!open) {
    return (
      <button
        onClick={handleOpen}
        aria-label="Open chat"
        style={{
          position: 'fixed',
          bottom: bottomOffset,
          [isRight ? 'right' : 'left']: 20,
          width: 56,
          height: 56,
          borderRadius: '50%',
          background: t.primaryColor,
          color: '#fff',
          border: 'none',
          cursor: 'pointer',
          fontSize: 24,
          fontFamily: t.fontFamily,
          boxShadow: '0 4px 14px rgba(0,0,0,0.18)',
          zIndex: t.zIndex + 9,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          transition: 'transform 0.2s, box-shadow 0.2s',
        }}
        onMouseEnter={(e) => {
          (e.currentTarget as HTMLButtonElement).style.transform = 'scale(1.08)';
        }}
        onMouseLeave={(e) => {
          (e.currentTarget as HTMLButtonElement).style.transform = 'scale(1)';
        }}
      >
        <span role="img" aria-label="chat">💬</span>
      </button>
    );
  }

  // ---------- Expanded: Chat panel ----------
  return (
    <div
      style={{
        position: 'fixed',
        bottom: bottomOffset,
        [isRight ? 'right' : 'left']: 20,
        width: 380,
        height: 520,
        borderRadius: t.borderRadius + 4,
        background: t.backgroundColor,
        boxShadow: '0 8px 30px rgba(0,0,0,0.15)',
        display: 'flex',
        flexDirection: 'column',
        fontFamily: t.fontFamily,
        zIndex: t.zIndex + 9,
        overflow: 'hidden',
        border: `1px solid ${t.borderColor}`,
      }}
    >
      {/* ── Header ── */}
      <div
        style={{
          background: t.primaryColor,
          color: '#fff',
          padding: '14px 16px',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexShrink: 0,
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ fontSize: 18 }}>💬</span>
          <span style={{ fontSize: 15, fontWeight: 600 }}>{chatTitle}</span>
        </div>
        <button
          onClick={() => setOpen(false)}
          aria-label="Minimize chat"
          style={{
            background: 'rgba(255,255,255,0.2)',
            border: 'none',
            color: '#fff',
            cursor: 'pointer',
            fontSize: 16,
            width: 28,
            height: 28,
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            lineHeight: 1,
          }}
        >
          &#x2715;
        </button>
      </div>

      {/* ── Messages ── */}
      <div
        style={{
          flex: 1,
          overflowY: 'auto',
          padding: 14,
          display: 'flex',
          flexDirection: 'column',
          gap: 10,
          background: t.surfaceColor,
        }}
      >
        {messages.map((m, i) => {
          const isUser = m.role === 'user';
          return (
            <div
              key={i}
              style={{
                display: 'flex',
                justifyContent: isUser ? 'flex-end' : 'flex-start',
              }}
            >
              <div
                style={{
                  background: isUser ? t.primaryColor : t.backgroundColor,
                  color: isUser ? '#fff' : t.textColor,
                  padding: '10px 14px',
                  borderRadius: t.borderRadius,
                  maxWidth: '80%',
                  fontSize: 14,
                  lineHeight: 1.5,
                  boxShadow: isUser
                    ? 'none'
                    : '0 1px 3px rgba(0,0,0,0.06)',
                  border: isUser ? 'none' : `1px solid ${t.borderColor}`,
                  wordBreak: 'break-word',
                }}
              >
                {m.content}
              </div>
            </div>
          );
        })}

        {/* Loading indicator */}
        {sending && (
          <div style={{ display: 'flex', justifyContent: 'flex-start' }}>
            <div
              style={{
                background: t.backgroundColor,
                border: `1px solid ${t.borderColor}`,
                padding: '10px 14px',
                borderRadius: t.borderRadius,
                fontSize: 14,
                color: t.textSecondary,
                display: 'flex',
                alignItems: 'center',
                gap: 4,
              }}
            >
              <LoadingDots color={t.textSecondary} />
            </div>
          </div>
        )}

        {/* Error message */}
        {error && (
          <div
            style={{
              padding: '8px 12px',
              background: '#FEF2F2',
              color: t.errorColor,
              borderRadius: t.borderRadius - 2,
              fontSize: 13,
              textAlign: 'center',
            }}
          >
            {error}
          </div>
        )}

        <div ref={bottomRef} />
      </div>

      {/* ── Input ── */}
      <form
        onSubmit={(e) => {
          e.preventDefault();
          handleSend();
        }}
        style={{
          display: 'flex',
          borderTop: `1px solid ${t.borderColor}`,
          background: t.backgroundColor,
          flexShrink: 0,
        }}
      >
        <input
          ref={inputRef}
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder="Type a message..."
          disabled={sending}
          style={{
            flex: 1,
            border: 'none',
            outline: 'none',
            padding: '14px 16px',
            fontSize: 14,
            fontFamily: t.fontFamily,
            background: 'transparent',
            color: t.textColor,
          }}
        />
        <button
          type="submit"
          disabled={sending || !input.trim()}
          aria-label="Send message"
          style={{
            background: t.primaryColor,
            color: '#fff',
            border: 'none',
            padding: '0 18px',
            cursor: sending || !input.trim() ? 'default' : 'pointer',
            fontSize: 16,
            opacity: sending || !input.trim() ? 0.5 : 1,
            transition: 'opacity 0.15s',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <span role="img" aria-label="send">&#9654;</span>
        </button>
      </form>
    </div>
  );
}

// ---------------------------------------------------------------------------
// LoadingDots — Simple animated dots to indicate AI thinking
// ---------------------------------------------------------------------------

function LoadingDots({ color }: { color: string }) {
  const [frame, setFrame] = useState(0);

  useEffect(() => {
    const timer = setInterval(() => setFrame((f) => (f + 1) % 4), 400);
    return () => clearInterval(timer);
  }, []);

  const dots = '.'.repeat(frame || 1);

  return (
    <span
      style={{
        color,
        fontSize: 16,
        fontWeight: 700,
        letterSpacing: 2,
        minWidth: 24,
        display: 'inline-block',
      }}
    >
      {dots}
    </span>
  );
}
